import React from 'react';
import { Card, CardContent, Typography } from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  BarChart,
  Bar
} from 'recharts';

export const CumulativeLineChart = ({ 
  data, 
  field, 
  title,
  height = 400,
  formatDecimals = 2 
}) => {
  // Sort data by created_on date
  const sortedData = [...data].sort((a, b) => 
    new Date(a.created_on) - new Date(b.created_on)
  );

  // Calculate cumulative sum
  let cumulativeSum = 0;
  const chartData = sortedData.map(item => {
    cumulativeSum += item[field] || 0;
    return {
      timestamp: new Date(item.created_on).toLocaleTimeString(),
      value: Number(cumulativeSum.toFixed(formatDecimals))
    };
  });

  // Calculate min and max for domain padding
  const values = chartData.map(item => item.value);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const padding = (maxValue - minValue) * 0.1;

  return (
    <Card sx={{ width: '100%', minHeight: height }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="body1" gutterBottom color="text.secondary">
          {title || `Cumulative ${field}`}
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart
            data={chartData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 50
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            {/* XAxis intentionally hidden for compact mobile view */}
            <YAxis 
              domain={[
                minValue - padding,
                maxValue + padding
              ]}
              tick={false}  // This hides the Y-axis tick marks and labels
              axisLine={false}  // Optional: removes the Y-axis line if desired
            />
            <Tooltip 
              formatter={(value) => [
                value.toFixed(formatDecimals),
                'Value'
              ]}
            />
            <ReferenceLine 
              y={0} 
              stroke="red" 
              strokeDasharray="3 3"
              label={{
                position: 'insideTopRight',
                value: '0 - Break even',
                fill: 'red'
              }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#8884d8"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export const TradesBarChart = ({ data, height = 200 }) => {
  const sortedData = [...data].sort((a, b) => new Date(a.created_on) - new Date(b.created_on));
  const chartData = sortedData.map((t, idx) => ({ index: idx + 1, pl: t.pl || 0 }));
  return (
    <Card sx={{ width: '100%', minHeight: height }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="body1" gutterBottom color="text.secondary">
          Per-trade P/L (pips)
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <YAxis tick={false} axisLine={false} />
            <Tooltip formatter={(value) => [Number(value).toFixed(2), 'P/L']} />
            <ReferenceLine y={0} stroke="red" strokeDasharray="3 3" />
            <Line type="step" dataKey="pl" stroke="#4caf50" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export const TimeframeChart = ({ data, height = 200 }) => {
  const timeframeStats = data.reduce((acc, trade) => {
    const tf = trade.time_frame || 'Unknown';
    if (!acc[tf]) {
      acc[tf] = { count: 0, totalPl: 0, wins: 0 };
    }
    acc[tf].count++;
    acc[tf].totalPl += trade.pl || 0;
    if (trade.pl >= 0) acc[tf].wins++;
    return acc;
  }, {});

  // Define the order for timeframes
  const timeframeOrder = ['1min', '5min', '15min', '1hr', '4hr'];
  
  const chartData = Object.entries(timeframeStats)
    .map(([timeframe, stats]) => ({
      timeframe,
      avgPl: stats.totalPl / stats.count,
      winRate: (stats.wins / stats.count) * 100,
      count: stats.count
    }))
    .sort((a, b) => {
      const aIndex = timeframeOrder.indexOf(a.timeframe);
      const bIndex = timeframeOrder.indexOf(b.timeframe);
      // If timeframe not in order list, put it at the end
      if (aIndex === -1 && bIndex === -1) return a.timeframe.localeCompare(b.timeframe);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

  return (
    <Card sx={{ width: '100%', minHeight: height }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="body1" gutterBottom color="text.secondary">
          Avg P/L by Timeframe
        </Typography>
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timeframe" tick={{ fontSize: 12 }} />
            <YAxis tick={false} axisLine={false} />
            <Tooltip 
              formatter={(value, name) => [
                `${Number(value).toFixed(2)} pips`,
                'Avg P/L'
              ]}
              labelFormatter={(label) => `Timeframe: ${label}`}
            />
            <ReferenceLine y={0} stroke="red" strokeDasharray="3 3" />
            <Bar 
              dataKey="avgPl" 
              fill="#2196f3"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
